import {
  CollectingCompanyTableItem,
  DangerousDEEEItem,
  DangerousDEEEPayloadItem,
  NonDangerousDEEEItem,
  NonDangerousDEEEPayloadItem
} from 'components/waste-management/interfaces'
import {
  isDangerousDEEEItem,
  isNonDangerousDEEEItem,
  getDailyReportName,
  getCurrentCompanyDescription
} from './helpers'
import { formatPayloadDate } from 'utils/helpers'

export const toDailyReportsOptions = (dailyReports: any[]) =>
  dailyReports.map(report => ({
    label: getDailyReportName({ reportNo: report.name, creationDate: report.creationDate }),
    value: report.id
  }))

const nonDangerousDEEEPayload = (values: NonDangerousDEEEItem) => ({
  wasteType: values.wasteType,
  wasteCompanyName: values.wasteCompanyName || values.companyName,
  wasteCategories: values.wasteCategories,
  wasteCode: values.wasteCode,
  operationTypes: values.operationTypes.map((type: AutocompleteOption) => `${type.value}`),
  measuredWeight: +values.measuredWeight,
  expeditorEnvironmentAuthorizationNumber: values.expeditorEnvironmentAuthorizationNumber,
  expeditorEnvironmentAuthorizationExpirationDate:
    values.expeditorEnvironmentAuthorizationExpirationDate,
  carrier: {
    id: values.carrierId,
    name: values.carrierName,
    cif: values.cif,
    headquarter: values.headquarter
  },
  delegate: {
    id: values.delegateId,
    name: values.delegateName,
    ...(values.identityCardSeries && { identityCardSeries: values.identityCardSeries }),
    ...(values.identityCardNumber && { identityCardNumber: values.identityCardNumber }),
    ...(values.cnp && { cnp: values.cnp }),
    vehicleNumber: values.vehicleNumber,
    transportLicenseNumber: values.transportLicenseNumber,
    transportLicenseExpirationDate: values.transportLicenseExpirationDate
  },
  environmentalAuthorizationNo: values.environmentalAuthorizationNo,
  environmentalPermitExpirationDate: values.environmentalPermitExpirationDate,
  ...(values.downloadDate && { downloadDate: formatPayloadDate(values.downloadDate) }),
  ...(values.companyName && {
    wasteCollectionCompany: {
      ...(values.companyId && { id: values.companyId }),
      name: values.companyName,
      cif: values.companyCif,
      headquarter: values.companyHeadquarter,
      tradeRegisterNumber: values.companyTradeRegisterNumber,
      county: values.companyCounty
    }
  })
})

const dangerousDEEEPayload = (values: DangerousDEEEItem) => ({
  wasteType: values.wasteType,
  wasteCompanyName: values.wasteCompanyName || values.companyName,
  hazardousWasteName: values.hazardousWasteName,
  wasteCode: values.wasteCode,
  wasteWeightPerYear: values.wasteWeightPerYear === 'true',
  transportApprovalFormNumber: values.transportApprovalFormNumber,
  measuredWeight: +values.measuredWeight,
  carrierReceivedWeight: +values.carrierReceivedWeight,
  carrier: {
    id: values.carrierId,
    name: values.carrierName,
    cif: values.cif,
    headquarter: values.headquarter,
    tradeRegisterNumber: values.carrierTradeRegisterNumber
  },
  delegate: {
    id: values.delegateId,
    name: values.delegateName,
    vehicleNumber: values.vehicleNumber
  },
  operationTypes: values.operationTypes.map((type: AutocompleteOption) => `${type.value}`),
  wasteCollectionCompanyReceivedWeight: values.wasteCollectionCompanyReceivedWeight,
  wasteCollectionCompanyRejectedWeight: values.wasteCollectionCompanyRejectedWeight,
  wasteCollectionCompanyReceivedDate: formatPayloadDate(values.wasteCollectionCompanyReceivedDate),
  ...(values.companyName && {
    wasteCollectionCompany: {
      ...(values.companyId && { id: values.companyId }),
      name: values.companyName,
      cif: values.companyCif,
      headquarter: values.companyHeadquarter,
      tradeRegisterNumber: values.companyTradeRegisterNumber,
      county: values.companyCounty
    }
  })
})

export const toWasteReportPayload = (
  values: DangerousDEEEItem | NonDangerousDEEEItem,
  isDangerousDEEE: boolean,
  isExternalArticlesReport?: boolean
): DangerousDEEEPayloadItem | NonDangerousDEEEPayloadItem => {
  let payload: DangerousDEEEPayloadItem | NonDangerousDEEEPayloadItem

  if (isDangerousDEEE && isDangerousDEEEItem(values)) {
    payload = dangerousDEEEPayload(values)
  } else if (!isDangerousDEEE && isNonDangerousDEEEItem(values)) {
    payload = nonDangerousDEEEPayload(values as NonDangerousDEEEItem)
  } else {
    throw new Error('Invalid item type')
  }

  if (isExternalArticlesReport) {
    const wasteCategoryIds = Array.isArray(values.wasteCategory)
      ? values.wasteCategory.map(category => category.value)
      : values.wasteCategory?.value
      ? [values.wasteCategory.value]
      : []

    if (wasteCategoryIds.length) {
      return { ...payload, wasteCategoryIds }
    }
  }

  return payload
}

export const toCurrentCompany = (company: CollectingCompanyTableItem) => ({
  description: getCurrentCompanyDescription(company),
  companyName: company.name,
  companyId: company.id,
  companyCif: company.cif,
  companyHeadquarter: company.headquarter,
  companyTradeRegisterNumber: company.tradeRegisterNumber,
  companyCounty: company.county,
  environmentalPermitNumber: company.environmentalPermitNumber,
  environmentalPermitExpirationDate: company.environmentalPermitExpirationDate,
  tradeRegisterNumber: company.tradeRegisterNumber
})
